// src/components/ui/PageLoader.jsx - Loader para transições de página
import { useEffect, useState } from 'react';
import logoPsiando from '@/assets/icon/logo_psiando.jpeg';


export const PageLoader = ({ 
  loading = true,
  message = 'Carregando página...',
  showProgress = false,
  progress = 0,
  fadeOut = true,
  delay = 0
}) => {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(loading);

  // Controlar visibilidade com delay
  useEffect(() => {
    if (loading) {
      setShouldRender(true);
      const timer = setTimeout(() => {
        setVisible(true);
      }, delay);
      return () => clearTimeout(timer);
    } else if (fadeOut) {
      setVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
      setShouldRender(false);
    }
  }, [loading, delay, fadeOut]);

  if (!shouldRender) return null;

  return (
    <div 
      className={`
        fixed inset-0 z-50 bg-white flex items-center justify-center
        transition-opacity duration-300 ease-in-out
        ${visible ? 'opacity-100' : 'opacity-0'}
      `}
    >
      <div className="text-center max-w-md px-6">
        {/* Logo animado */}
        <div className="mb-8">
          <PsiandoAnimatedLogo />
        </div>

        {/* Mensagem */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {message}
          </h2>
          <p className="text-gray-600 text-sm">
            Preparando sua experiência...
          </p>
        </div>

        {/* Barra de progresso (opcional) */}
        {showProgress && (
          <div className="mb-4">
            <ProgressBar progress={progress} />
          </div>
        )}

        {/* Spinner de loading */}
        <LoadingDots />
      </div>
    </div>
  );
};

// Logo circular — mesmo padrão do Header/MainLayout
const LogoCircle = ({ size = 64 }) => (
  <div style={{
    width: size, height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    border: '3px solid var(--primary-accent)',
    boxShadow: '0 4px 16px rgba(26,92,88,0.25)',
    flexShrink: 0,
  }}>
    <img
      src={logoPsiando}
      alt="Psiando"
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  </div>
);

const PsiandoAnimatedLogo = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      {/* Anel rotativo externo */}
      <div className="absolute animate-spin" style={{
        inset: -8, borderRadius: '50%',
        border: '2px solid transparent',
        borderTopColor: 'var(--primary-accent)',
        borderRightColor: 'var(--secondary-light)',
        animationDuration: '3s',
      }} />
      <LogoCircle size={96} />
    </div>
  </div>
);

// Barra de progresso
const ProgressBar = ({ progress = 0 }) => {
  return (
    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
      <div 
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{
          background: "linear-gradient(90deg, var(--primary-main), var(--primary-accent))",
          width: `${Math.min(100, Math.max(0, progress))}%`
        }}
      ></div>
    </div>
  );
};

// Loading dots animados
const LoadingDots = () => {
  return (
    <div className="flex justify-center items-center space-x-2">
      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--primary-main)', animationDelay: '0ms' }}></div>
      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--primary-accent)', animationDelay: '150ms' }}></div>
      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'var(--secondary-light)', animationDelay: '300ms' }}></div>
    </div>
  );
};

// Loader para componentes específicos (não fullscreen)
export const ComponentLoader = ({ 
  message = 'Carregando...',
  size = 'md',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'py-4',
    md: 'py-8',
    lg: 'py-12'
  };

  return (
    <div className={`flex flex-col items-center justify-center ${sizeClasses[size]} ${className}`}>
      <div className="mb-4">
        <MiniSpinner size={size} />
      </div>
      <p className="text-gray-600 text-sm">
        {message}
      </p>
    </div>
  );
};

const MiniSpinner = ({ size = 'md' }) => {
  const sizeMap = { sm: 32, md: 40, lg: 56 };
  return (
    <div className="flex items-center justify-center">
      <LogoCircle size={sizeMap[size] || 40} />
    </div>
  );
};

// Loader para tabelas e listas
export const TableLoader = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="animate-pulse">
      {/* Header */}
      <div className="flex space-x-4 mb-4">
        {Array.from({ length: columns }).map((_, i) => (
          <div key={i} className="flex-1 h-4 bg-gray-200 rounded"></div>
        ))}
      </div>
      
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex space-x-4 mb-3">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div 
              key={colIndex} 
              className={`flex-1 h-3 bg-gray-100 rounded ${
                Math.random() > 0.5 ? 'w-3/4' : 'w-full'
              }`}
            ></div>
          ))}
        </div>
      ))}
    </div>
  );
};

// Loader para cards
export const CardLoader = ({ count = 3 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
          {/* Header */}
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full mr-3"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          </div>
          
          {/* Content */}
          <div className="space-y-3">
            <div className="h-3 bg-gray-100 rounded"></div>
            <div className="h-3 bg-gray-100 rounded w-5/6"></div>
            <div className="h-3 bg-gray-100 rounded w-4/6"></div>
          </div>
          
          {/* Footer */}
          <div className="flex justify-between items-center mt-6">
            <div className="h-8 bg-gray-200 rounded w-20"></div>
            <div className="h-8 bg-gray-200 rounded w-16"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Loader para texto/conteúdo
export const ContentLoader = ({ lines = 5, width = 'full' }) => {
  const widthClasses = {
    full: 'w-full',
    '3/4': 'w-3/4',
    '1/2': 'w-1/2'
  };

  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: lines }).map((_, index) => (
        <div 
          key={index}
          className={`h-4 bg-gray-200 rounded ${
            index === lines - 1 ? 'w-2/3' : widthClasses[width]
          }`}
        ></div>
      ))}
    </div>
  );
};

// Hook para controlar loading de página
export const usePageLoader = (initialLoading = true) => {
  const [loading, setLoading] = useState(initialLoading);
  const [progress, setProgress] = useState(0);

  const startLoading = (message) => {
    setLoading(true);
    setProgress(0);
  };

  const updateProgress = (newProgress) => {
    setProgress(newProgress);
  };

  const finishLoading = () => {
    setProgress(100);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  return {
    loading,
    progress,
    startLoading,
    updateProgress,
    finishLoading,
    setLoading
  };
};

export default PageLoader;